import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap";
import { CheckCircle, Copy, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";

const HubspotCallback = () => {
  const { t } = useTranslation();
  const [urlParams, setUrlParams] = useState({});
  const [copied, setCopied] = useState(false);
  let history = useHistory();

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);
    const parsedParams = {};

    for (let [key, value] of params) {
      parsedParams[key] = value;
    }

    setUrlParams(parsedParams);
  }, []);

  // TanStack Query to verify OAuth status
  const {
    data: verificationData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["oauth-verification", urlParams.code, urlParams.state],
    queryFn: async () => {
      if (!urlParams.code || !urlParams.state) {
        throw new Error(t("oauth.error.missingRequiredParams"));
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const response = await fetch(
        `${baseUrl}/oauth-callback?code=${encodeURIComponent(
          urlParams.code
        )}&state=${encodeURIComponent(urlParams.state)}`
      );

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = t("oauth.error.httpError", {
          status: response.status,
        });
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = translateApiError(errorData);
          }
          if (errorData.code === "HUBSPOT_PORTAL_ALREADY_CONNECTED") {
            throw new Error(errorMessage);
          }
        } catch (parseError) {
          // If parsing fails, use default error message
        }
        throw new Error(errorMessage);
      }

      return response.json();
    },
    enabled: !!(urlParams.code && urlParams.state), // Only run if we have both parameters
    retry: 2,
    retryDelay: 1000,
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isSuccess =
    verificationData?.status === "success" || verificationData?.status === true;
  const hasRequiredParams =
    urlParams.code && urlParams.state && !urlParams.error;

  // Loading state
  if (isLoading && hasRequiredParams) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md="6" lg="4">
              <Card className="shadow-lg border-0">
                <CardBody className="text-center p-5">
                  <div className="mb-4">
                    <Spinner
                      color="primary"
                      style={{ width: "4rem", height: "4rem" }}
                      className="mb-3"
                    />
                    <h2 className="h3 font-weight-bold text-dark mb-3">
                      {t("oauth.verifying.title")}
                    </h2>
                    <p className="text-muted">{t("oauth.verifying.text")}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e8f5e8 0%, #e3f2fd 100%)",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col md="6" lg="5">
            <Card className="shadow-lg border-0">
              <CardBody className="text-center p-5">
                {isSuccess && !isError && hasRequiredParams ? (
                  <>
                    <div className="mb-4">
                      <CheckCircle size={64} className="text-success mb-3" />
                      <h2 className="h3 font-weight-bold text-dark mb-3">
                        {t("oauth.success.title")}
                      </h2>
                      <p className="text-muted">{t("oauth.success.text")}</p>
                    </div>

                    {/*<Card className="bg-light border-0 mb-4">
                      <CardBody className="p-3">
                        <h5 className="h6 font-weight-bold text-primary mb-3">
                          Callback Details
                        </h5>

                        {urlParams.code && (
                          <div className="mb-3">
                            <small className="text-muted d-block mb-1 font-weight-medium">
                              Authorization Code
                            </small>
                            <InputGroup size="sm">
                              <Input
                                type="text"
                                value={urlParams.code}
                                readOnly
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  backgroundColor: "white",
                                }}
                              />
                              <InputGroupText
                                style={{
                                  cursor: "pointer",
                                  padding: "0.25rem 0.5rem",
                                }}
                                onClick={() => copyToClipboard(urlParams.code)}
                                title="Copy code"
                              >
                                <Copy size={14} />
                              </InputGroupText>
                            </InputGroup>
                          </div>
                        )}

                        {urlParams.state && (
                          <div className="mb-3">
                            <small className="text-muted d-block mb-1 font-weight-medium">
                              State Parameter
                            </small>
                            <InputGroup size="sm">
                              <Input
                                type="text"
                                value={urlParams.state}
                                readOnly
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  backgroundColor: "white",
                                }}
                              />
                              <InputGroupText
                                style={{
                                  cursor: "pointer",
                                  padding: "0.25rem 0.5rem",
                                }}
                                onClick={() => copyToClipboard(urlParams.state)}
                                title="Copy state"
                              >
                                <Copy size={14} />
                              </InputGroupText>
                            </InputGroup>
                          </div>
                        )}

                        {copied && (
                          <Badge color="success" className="mt-2">
                            ✓ Copied to clipboard
                          </Badge>
                        )}
                      </CardBody>
                    </Card> */}

                    {/* <div className="d-grid gap-2">
                      <Button
                        color="primary"
                        size="lg"
                        onClick={() => history.push("/integration")}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <ExternalLink size={16} className="me-2" />
                        Return to App
                      </Button>
                    </div> */}
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <div
                        className="bg-danger bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                        style={{ width: "4rem", height: "4rem" }}
                      >
                        <span style={{ fontSize: "2rem" }}>⚠️</span>
                      </div>
                      <h2 className="h3 font-weight-bold text-dark mb-3">
                        {t("oauth.error.title")}
                      </h2>
                      <p className="text-muted mb-3">
                        {isError && error
                          ? t("oauth.error.verifyIssue", {
                              message: error.message,
                            })
                          : !hasRequiredParams
                          ? t("oauth.error.missingParams")
                          : t("oauth.error.verifyFailed")}
                      </p>

                      {isError && (
                        <Alert color="danger" className="text-start">
                          <strong>{t("oauth.error.details")}</strong>{" "}
                          {error?.message || t("oauth.error.unknown")}
                        </Alert>
                      )}
                    </div>

                    <div className="d-grid gap-2">
                      <Button
                        color="warning"
                        size="lg"
                        onClick={() => window.location.reload()}
                        className="mb-2"
                      >
                        {t("oauth.tryAgain")}
                      </Button>

                      <Button
                        color="primary"
                        size="lg"
                        onClick={() => window.close()}
                      >
                        {t("oauth.returnToApp")}
                      </Button>
                    </div>
                  </>
                )}

                <hr className="my-4" />
                <small className="text-muted">
                  {t("oauth.contactSupport")}
                </small>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HubspotCallback;
